import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { MultiStackedColumnChart } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import {
  patientAccessTheme,
  Typography,
} from '@HomaHealth/ui-component-library';
import InfoGraphicBar from '../../atoms/Icons/InfoGraphicBar';
import NoTabsData from '../../molecules/NoTabsData';
import {
  CostTrendChartType,
  formatCostTrend,
} from '../../../core-utils/helper-utils/summaryReportHelper';
import axios from 'axios';
import properties from '../../../core-utils/properties';
import { useAPIPayload } from '../../../core-utils/helper-utils/apiPayloadHelper';
import { useStores } from '../../../stores';
import Loader from '../../molecules/Loader';
import DeepDiveCostTrendModal from '../DeepDiveCostTrendModal';

const useStyles = makeStyles()({
  chartRelativeContainer: {
    padding: '5px',
  },
  // mainInnerContainer: {
  //   display: 'flex',
  //   justifyContent: 'center',
  //   alignItems: 'center',
  //   height: '500px',
  // },
  noDataContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '540px',
    border: '1px solid #E5E9F0',
    borderRadius: '4px',
  },
  outerCont: {
    height: '500px',
    border: '1px solid #E5E9F0',
    borderRadius: '4px',
  },
});

interface SpendTrendProps {
  // CostTrendChartType: CostTrendChartType;
  incurredStartDate: any;
  incurredEndDate: any;
  paidStartDate: any;
  paidEndDate: any;
  isPlanDatesFetched: any;
  aggregationParameter: any;
  isIncurredDate: boolean;
  isPaidDate: boolean;
  totalCost: string;
}

const SpendTrend: React.FC<SpendTrendProps> = ({
  // CostTrendChartType
  incurredStartDate,
  incurredEndDate,
  paidStartDate,
  paidEndDate,
  aggregationParameter,
  isIncurredDate,
  isPaidDate,
  totalCost,
}) => {
  const { classes } = useStyles();
  const payload = useAPIPayload();
  const cohortDetailsStore = useStores().cohortDetailsStore;
  const abortController = new AbortController();
  const [loading, setLoading] = useState(true);
  const [mainLoader] = useState<any>(<Loader height="400px" />);

  const [
    CostTrendChartType,
    setCostTrendChartType,
  ] = useState<CostTrendChartType>({
    medicalClaimsData: [],
    medicalTotalPaid: [],
    medicalPlanPaid: [],
    medicalDeductible: [],
    medicalCopay: [],
    medicalCoinsurance: [],
    rxClaims: [],
    rxTotalPaid: [],
    rxPlanPaid: [],
    rxDeductible: [],
    rxCopay: [],
    rxCoinsurance: [],
    xAxisValues: [],
  });
  const [costTrendModalOpen, setCostTrendModalOpen] = useState<boolean>(false);

  // function to check no data

  const isCostTrendEmpty = (data: CostTrendChartType): boolean => {
    return [
      data?.medicalClaimsData,
      data?.medicalTotalPaid,
      data?.rxTotalPaid,
      data?.rxClaims,
      data?.xAxisValues,
    ].every((arr) => arr?.length === 0);
  };

  const getSpendTrendData = async () => {
    try {
      setLoading(true);

      const responses = await axios.post(
        properties.aiqBaseUrl +
          properties.summaryBaseUrl +
          properties.costTrend,
        {
          ...payload,
          claim_id_list: cohortDetailsStore.claimIdList,
          member_unique_id_list: cohortDetailsStore.memberUniqueIdList,
        },
        { signal: abortController.signal },
      );
      setCostTrendChartType(formatCostTrend(responses?.data?.data?.costTrend));
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error('Error in spend trend', err);
    }
  };

  useEffect(() => {
    getSpendTrendData();
  }, [
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    aggregationParameter,
  ]);

  return (
    <>
      {loading ? (
        mainLoader
      ) : !isCostTrendEmpty(CostTrendChartType) ? (
        <Grid className={classes.outerCont}>
          <Grid className={classes.chartRelativeContainer}>
            <MultiStackedColumnChart
              subTitle="Click to view full historical trend."
              handleClickHere={() => setCostTrendModalOpen(true)}
              widthOfChart={850}
              heightOfChart={460}
              cardStyles={{
                border: 'none',
                boxShadow: 'none',
                borderRadius: '0px',
                padding: '0px',
                background: 'none',
              }}
              category={CostTrendChartType?.xAxisValues}
              dataSet={[
                {
                  dataset: [
                    {
                      seriesname: 'Medical Plan Paid',
                      color: '#2B376A',
                      data: CostTrendChartType?.medicalPlanPaid,
                    },
                    {
                      seriesname: 'Medical Deductible',
                      color: '#6C7DC0',
                      data: CostTrendChartType?.medicalDeductible,
                    },
                    {
                      seriesname: 'Medical Copay',
                      color: '#919DD0',
                      data: CostTrendChartType?.medicalCopay,
                    },
                    {
                      seriesname: 'Medical Coinsurance',
                      color: '#B5BEDF',
                      data: CostTrendChartType?.medicalCoinsurance,
                    },
                    {
                      seriesname: 'Rx Plan Paid',
                      color: '#FF8A53',
                      data: CostTrendChartType?.rxPlanPaid,
                    },
                    {
                      seriesname: 'Rx Deductible',
                      color: '#FC8851',
                      data: CostTrendChartType?.rxDeductible,
                    },
                    {
                      seriesname: 'Rx Copay',
                      color: '#FF9B6D',
                      data: CostTrendChartType?.rxCopay,
                    },
                    {
                      seriesname: 'Rx Coinsurance',
                      color: '#FFAA82',
                      data: CostTrendChartType?.rxCoinsurance,
                    },
                  ],
                },
              ]}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid className={classes.noDataContainer}>
          <NoTabsData
            icon={<InfoGraphicBar />}
            label="Spend trend data unavailable."
          />
          <Typography
            textColor={patientAccessTheme.palette.text.lowemphasis}
            fontSize="12px"
          >
            We couldn't find any spend records for the selected period or
            criteria
          </Typography>
        </Grid>
      )}

      {costTrendModalOpen && (
        <DeepDiveCostTrendModal
          open={costTrendModalOpen}
          setOpen={setCostTrendModalOpen}
          selectedTrend={{
            value: 'Overall',
            total_paid_amount: totalCost,
          }}
          payload={payload}
          isIncurredDate={isIncurredDate}
          isPaidDate={isPaidDate}
        />
      )}
    </>
  );
};

export default SpendTrend;
